import {
  createContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { User } from '../types/models';
import {
  login as apiLogin,
  register as apiRegister,
  me as apiMe,
} from '../services/auth';
import { AUTH_ERROR_EVENT, clearToken, getToken } from '../services/api';

interface AuthContextValue {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName?: string) => Promise<void>;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setTokenState] = useState<string | null>(getToken());
  const [isLoading, setIsLoading] = useState<boolean>(!!getToken());

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      if (!getToken()) {
        setIsLoading(false);
        return;
      }
      try {
        const { user: u } = await apiMe();
        if (!cancelled) {
          setUser(u);
          setTokenState(getToken());
        }
      } catch {
        if (!cancelled) {
          clearToken();
          setUser(null);
          setTokenState(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onAuthError = () => {
      setUser(null);
      setTokenState(null);
    };
    window.addEventListener(AUTH_ERROR_EVENT, onAuthError);
    return () => window.removeEventListener(AUTH_ERROR_EVENT, onAuthError);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiLogin(email, password);
    setUser(res.user);
    setTokenState(res.token);
  }, []);

  const register = useCallback(async (email: string, password: string, displayName?: string) => {
    const res = await apiRegister(email, password, displayName);
    setUser(res.user);
    setTokenState(res.token);
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
    setTokenState(null);
  }, []);

  const value: AuthContextValue = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    login,
    register,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
